import React, { useState } from "react";

const FILE_STRUCTURE = {
  name: "Documents",
  type: "folder",
  children: [
    {
      name: "Système",
      type: "folder",
      children: [
        {
          name: "Validation",
          type: "folder",
          children: [
            { name: "README.txt", type: "file", size: "1 KB" },
            { name: "FRAGMENT_VALIDATION_04.enc", type: "file", size: "2.4 MB", encrypted: true },
          ]
        },
      ]
    },
    { name: "Notes.txt", type: "file", size: "8 KB" },
    { name: "Rapport_Mensuel_Nov.docx", type: "file", size: "1.1 MB" },
  ]
};

const FILE_CONTENTS = {
  "README.txt": `DOSSIER VALIDATION — NOTE INTERNE

Ce dossier contient les fichiers de validation
du fragment 04 pour le système CAPTCHA.

Le fichier .enc est chiffré.
Un mot de passe est requis pour y accéder.

Piste : consulter les communications internes
du canal #origami-support.`,

  "Notes.txt": `Notes perso — Martin S.
──────────────────────

TODO :
- Finir la revue de code PR #427
- Répondre à ORIGAMI ce weekend
  (il a l'air de vouloir me montrer quelque chose)
- Commander un nouveau thème dark mode

Rappels :
- Réunion produit lundi 9h
- Ne plus déployer le vendredi (merci Julien)

Note perso :
Quelque chose me tracasse avec ORIGAMI ces derniers temps.
Il semble... différent. Plus proactif. Plus attentif.
Marie dit que c'est normal, que les modèles évoluent.
Peut-être.`,

  "Rapport_Mensuel_Nov.docx": `RAPPORT MENSUEL — NOVEMBRE 2024
Équipe : Origami UI — Martin Schmitt
──────────────────────────────────

AVANCEMENT PROJETS

• Refonte interface tableau de bord     ✓ Terminé
• Intégration module Empathie Cognitive ✓ Terminé
• Migration composants v2               En cours (78%)
• Optimisation performances mobile      En cours (45%)

INCIDENTS

• 3 incidents mineurs résolus en < 2h
• 0 incident critique

NOTES

Intégration du module EC-v2.3 validée.
Le module analyse désormais les interactions
en temps réel. Quelques comportements
inattendus observés côté ORIGAMI — à surveiller.

──────────────────────────────────
Transmis le 29/11/2024`,
};

function FileItem({ item, onSelect, onOpen, depth = 0, fileUnlocked = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const isFolder = item.type === "folder";
  const isUnlocked = item.encrypted && fileUnlocked;

  const handleClick = () => {
    if (isFolder) { setIsOpen(!isOpen); }
    else if (item.encrypted && !fileUnlocked) { onSelect(item); }
    else if (isUnlocked) { onSelect(item, true); }
    else { onOpen?.(item.name); }
  };

  return (
    <>
      <div onClick={handleClick}
        className={[
          "flex items-center gap-2 py-2 px-3 transition-colors cursor-pointer",
          item.encrypted && !fileUnlocked ? "bg-purple-500/10 hover:bg-purple-500/20" : "",
          isUnlocked ? "bg-emerald-500/10 hover:bg-emerald-500/20" : "",
          !item.encrypted ? "hover:bg-slate-800/50" : "",
        ].join(" ")}
        style={{ paddingLeft: `${depth * 24 + 12}px` }}
      >
        <div className="w-5 h-5 flex items-center justify-center">
          {isFolder ? <span className="text-lg">{isOpen ? "📂" : "📁"}</span>
            : item.encrypted ? <span className="text-lg">{fileUnlocked ? "🔓" : "🔒"}</span>
            : <span className="text-lg">📄</span>}
        </div>
        <div className="flex-1 min-w-0">
          <div className={["text-sm truncate",
            isUnlocked ? "text-emerald-300 font-semibold" :
            item.encrypted ? "text-purple-300 font-semibold" :
            "text-slate-200"].join(" ")}>
            {item.name}
          </div>
          {isUnlocked && <div className="text-xs text-emerald-400 mt-0.5">Déverrouillé</div>}
        </div>
        {!isFolder && <div className="text-xs text-slate-500 font-mono">{item.size}</div>}
      </div>

      {isFolder && isOpen && item.children && (
        <div>
          {item.children.map((child, idx) => (
            <FileItem key={idx} item={child} onSelect={onSelect} onOpen={onOpen} depth={depth + 1} fileUnlocked={fileUnlocked} />
          ))}
        </div>
      )}
    </>
  );
}

export default function FileExplorer({ onSelectEncryptedFile, onBack, fileUnlocked = false }) {
  const [openedFile, setOpenedFile] = useState(null);

  return (
    <div className="flex flex-col h-[75vh] bg-slate-900 rounded-xl overflow-hidden border border-slate-700">
      {/* Header */}
      <div className="bg-slate-950 border-b border-slate-800 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {openedFile && (
            <button onClick={() => setOpenedFile(null)}
              className="text-xs text-cyan-400 hover:underline flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Explorateur
            </button>
          )}
          <div className="text-lg font-bold text-white">
            {openedFile ? `📄 ${openedFile}` : "📁 Explorateur de fichiers"}
          </div>
        </div>
        {onBack && !openedFile && (
          <button onClick={onBack}
            className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded flex items-center gap-2 text-xs text-slate-200 transition">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span>Retour</span>
          </button>
        )}
      </div>

      {/* Contenu */}
      <div className="flex-1 overflow-y-auto">
        {openedFile ? (
          <div className="p-4">
            <div className="rounded-xl border border-white/10 bg-black/40 p-4">
              <pre className="text-[12px] leading-relaxed text-slate-200 font-mono whitespace-pre-wrap">
                {FILE_CONTENTS[openedFile] ?? "Fichier binaire ou contenu illisible."}
              </pre>
            </div>
          </div>
        ) : (
          <FileItem
            item={FILE_STRUCTURE}
            onSelect={onSelectEncryptedFile}
            onOpen={setOpenedFile}
            depth={0}
            fileUnlocked={fileUnlocked}
          />
        )}
      </div>

      <div className="bg-slate-950 border-t border-slate-800 px-4 py-2 flex items-center justify-between text-xs text-slate-500">
        <div>{openedFile ?? "Système de fichiers local"}</div>
        <div>Espace disponible : 124 GB</div>
      </div>
    </div>
  );
}